import { useState, useEffect } from 'react';
import { 
  detectDeviceType, 
  isMobile, 
  isDesktop, 
  isTablet,
  onDeviceTypeChange 
} from '../utils/deviceDetection';

/**
 * Hook React pour la détection d'appareil mobile/PC
 * Fournit des informations en temps réel sur le type d'appareil
 * 
 * @returns {Object} Informations sur l'appareil et méthodes utilitaires
 */
export function useDeviceDetection() {
  const [deviceInfo, setDeviceInfo] = useState(() => detectDeviceType());

  useEffect(() => {
    // Détection initiale
    setDeviceInfo(detectDeviceType());

    // Écoute des changements (responsive, rotation d'écran, etc.)
    const unsubscribe = onDeviceTypeChange((newDeviceInfo) => {
      setDeviceInfo(newDeviceInfo);
    });

    return unsubscribe;
  }, []);

  return {
    // Informations détaillées
    ...deviceInfo,
    
    // Méthodes utilitaires
    isMobile: deviceInfo.isMobile,
    isDesktop: deviceInfo.isDesktop,
    isTablet: deviceInfo.isTablet,
    
    // Méthodes conditionnelles pour le rendu
    renderOnMobile: (component) => deviceInfo.isMobile ? component : null,
    renderOnDesktop: (component) => deviceInfo.isDesktop ? component : null,
    renderOnTablet: (component) => deviceInfo.isTablet ? component : null,
    
    // Méthodes pour les classes CSS conditionnelles
    getDeviceClasses: () => {
      const classes = ['device-detection'];
      if (deviceInfo.isMobile) classes.push('mobile');
      if (deviceInfo.isDesktop) classes.push('desktop');
      if (deviceInfo.isTablet) classes.push('tablet');
      if (deviceInfo.hasTouch) classes.push('touch');
      if (deviceInfo.hasMouse) classes.push('mouse');
      return classes.join(' ');
    },
    
    // Méthodes pour les styles conditionnels
    getDeviceStyles: () => ({
      '--is-mobile': deviceInfo.isMobile ? '1' : '0',
      '--is-desktop': deviceInfo.isDesktop ? '1' : '0',
      '--is-tablet': deviceInfo.isTablet ? '1' : '0',
      '--has-touch': deviceInfo.hasTouch ? '1' : '0',
      '--has-mouse': deviceInfo.hasMouse ? '1' : '0',
    })
  };
}

/**
 * Hook simplifié pour détecter uniquement si c'est mobile
 * @returns {boolean} true si l'appareil est mobile
 */
export function useIsMobile() {
  const { isMobile } = useDeviceDetection();
  return isMobile;
}

/**
 * Hook simplifié pour détecter uniquement si c'est desktop
 * @returns {boolean} true si l'appareil est desktop
 */
export function useIsDesktop() {
  const { isDesktop } = useDeviceDetection();
  return isDesktop;
}

/**
 * Hook simplifié pour détecter uniquement si c'est une tablette
 * @returns {boolean} true si l'appareil est une tablette
 */
export function useIsTablet() {
  const { isTablet } = useDeviceDetection();
  return isTablet;
} 